import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AddCustomerDialog } from "@/components/add-customer-dialog";
import { AddJobDialog } from "@/components/add-job-dialog";
import { CustomerTable } from "@/components/customer-table";
import { PaymentsTable } from "@/components/payments-table";
import { OverdueTable } from "@/components/overdue-table";
import { SettingsPage } from "@/components/settings-page";
import { MultiSaleDialog } from "@/components/multi-sale-dialog";
import { CustomerPaymentSheet } from "@/components/customer-payment-sheet";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { Customer } from "@/types/customer";
import { Product } from "@/types/product";
import { Search, Users, Smartphone, Banknote, DollarSign, Package, ArrowLeft, Receipt, FileBarChart, Settings, LogOut, LayoutDashboard, Bell, ShoppingCart, CheckCircle, CreditCard, Menu, X, Shield, Trash2, MoreHorizontal, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PWAInstallButton } from "@/components/pwa-install-button";
import { useAuth } from "@/contexts/auth-context";
import { useDataService } from "@/hooks/use-data-service";
import { RealtimeStatus } from "@/components/realtime-status";
import { RealtimeNotifications } from "@/components/realtime-notifications";
import { useToast } from "@/contexts/toast-context";

interface AuthenticatedAppProps {
  onLogout: () => void;
}

export function AuthenticatedApp({ onLogout }: AuthenticatedAppProps) {
  const { success, error: showError } = useToast();
  const {
    customers,
    products,
    jobs,
    isLoading: dataLoading,
    isRealtimeConnected,
    isRealtimeEnabled,
    addCustomer,
    updateCustomer,
    deleteCustomer,
    addProduct,
    updateProduct,
    deleteProduct,
    updatePayment,
    handlePayment,
    addJob,
    deleteJob,
  } = useDataService();

  const [searchTerm, setSearchTerm] = useState("");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [activePage, setActivePage] = useState<"dashboard" | "customers" | "sales" | "payments" | "expenses" | "notifications" | "reports">("dashboard");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [paymentsSelectedCustomer, setPaymentsSelectedCustomer] = useState<Customer | null>(null);
  const [paymentsProfileOpen, setPaymentsProfileOpen] = useState(false);
  
  // Sales page state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [saleToDelete, setSaleToDelete] = useState<string | null>(null);
  const [expandedSales, setExpandedSales] = useState<Set<string>>(new Set());

  const toggleSaleExpanded = (saleId: string) => {
    setExpandedSales(prev => {
      const newSet = new Set<string>();
      if (!prev.has(saleId)) {
        newSet.add(saleId);
      }
      return newSet;
    });
  };

  // Apply saved theme and color on load
  useEffect(() => {
    const settingsStr = localStorage.getItem("qandil-settings");
    if (settingsStr) {
      const settings = JSON.parse(settingsStr);
      const root = document.documentElement;
      
      // Apply theme
      const isDark = settings.theme === "dark" || 
        (settings.theme === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches);
      
      if (settings.theme === "dark") {
        root.classList.add("dark");
      } else if (settings.theme === "light") {
        root.classList.remove("dark");
      } else {
        if (window.matchMedia("(prefers-color-scheme: dark)").matches) {
          root.classList.add("dark");
        } else {
          root.classList.remove("dark");
        }
      }

      // Apply primary color
      const COLORS: Record<string, { light: string; dark: string }> = {
        default: { light: "0 0% 9%", dark: "0 0% 98%" },
        blue: { light: "221.2 83.2% 53.3%", dark: "217.2 91.2% 59.8%" },
        green: { light: "142.1 76.2% 36.3%", dark: "142.1 70.6% 45.3%" },
        red: { light: "0 72.2% 50.6%", dark: "0 72.2% 50.6%" },
        purple: { light: "262.1 83.3% 57.8%", dark: "263.4 70% 50.4%" },
        orange: { light: "24.6 95% 53.1%", dark: "20.5 90.2% 48.2%" },
      };
      const colorData = COLORS[settings.primaryColor];
      if (colorData) {
        const hsl = isDark ? colorData.dark : colorData.light;
        root.style.setProperty("--primary", `hsl(${hsl})`);
        if (isDark) {
          root.style.setProperty("--primary-foreground", "hsl(0 0% 9%)");
        } else {
          root.style.setProperty("--primary-foreground", "hsl(0 0% 98%)");
        }
      }
    }
  }, []);

  // Body scroll lock when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      // Prevent body scroll
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      // Restore body scroll
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }

    // Cleanup function to restore scroll when component unmounts
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [mobileMenuOpen]);

  // Handle escape key to close mobile menu
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };

    if (mobileMenuOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [mobileMenuOpen]);

  const handleAddCustomer = async (customerData: Omit<Customer, "id" | "createdAt">) => {
    try {
      await addCustomer(customerData);
      success(`کڕیار بە سەرکەوتوویی زیادکرا: ${customerData.fullName}`);
    } catch (err) {
      console.error("Failed to add customer:", err);
      showError("هەڵەیەک ڕوویدا لە زیادکردنی کڕیار");
    }
  };

  const handleAddJob = async (job: string) => {
    try {
      await addJob(job);
      success(`پیشە زیادکرا: ${job}`);
    } catch (err) {
      console.error("Failed to add job:", err);
      showError("هەڵەیەک ڕوویدا لە زیادکردنی پیشە");
    }
  };

  const handleDeleteJob = async (job: string) => {
    try {
      await deleteJob(job);
      success(`پیشە سڕایەوە: ${job}`);
    } catch (err) {
      console.error("Failed to delete job:", err);
      showError("هەڵەیەک ڕوویدا لە سڕینەوەی پیشە");
    }
  };

  const handleAddProduct = async (productData: Omit<Product, "id" | "createdAt" | "remainingAmount">) => {
    try {
      await addProduct(productData);
      success(`فرۆشتن زیادکرا: ${productData.deviceName}`);
    } catch (err) {
      console.error("Failed to add product:", err);
      showError("هەڵەیەک ڕوویدا لە زیادکردنی فرۆشتن");
    }
  };

  const handleDeleteProduct = async (id: string) => {
    try {
      await deleteProduct(id);
      success("فرۆشتن سڕایەوە");
    } catch (err) {
      console.error("Failed to delete product:", err);
      showError("هەڵەیەک ڕوویدا لە سڕینەوەی فرۆشتن");
    }
  };

  const handleUpdatePayment = async (id: string, paidAmount: number) => {
    try {
      await updatePayment(id, paidAmount);
      success(`پارەدان نوێکرایەوە: ${paidAmount.toLocaleString()} دینار`);
    } catch (err) {
      console.error("Failed to update payment:", err);
      showError("هەڵەیەک ڕوویدا لە نوێکردنەوەی پارەدان");
    }
  };

  const handleUpdateProduct = async (updatedProduct: Product) => {
    try {
      await updateProduct(updatedProduct);
      success(`فرۆشتن نوێکرایەوە: ${updatedProduct.deviceName}`);
    } catch (err) {
      console.error("Failed to update product:", err);
      showError("هەڵەیەک ڕوویدا لە نوێکردنەوەی فرۆشتن");
    }
  };

  const handleUpdateCustomer = async (updatedCustomer: Customer) => {
    try {
      await updateCustomer(updatedCustomer);
      success(`کڕیار نوێکرایەوە: ${updatedCustomer.fullName}`);
    } catch (err) {
      console.error("Failed to update customer:", err);
      showError("هەڵەیەک ڕوویدا لە نوێکردنەوەی کڕیار");
    }
  };

  const handlePaymentAction = async (productId: string, amount: number) => {
    try {
      await handlePayment(productId, amount);
      success(`پارەدان تۆمارکرا: ${amount.toLocaleString()} دینار`);
    } catch (err) {
      console.error("Failed to process payment:", err);
      showError("هەڵەیەک ڕوویدا لە پارەدان");
    }
  };

  const customerCountByJob = customers.reduce((acc, customer) => {
    if (customer.job) {
      acc[customer.job] = (acc[customer.job] || 0) + 1;
    }
    return acc;
  }, {} as Record<string, number>);

  const handleDeleteCustomer = async (id: string) => {
    if (confirm("دڵنیایت لە سڕینەوەی ئەم کڕیارە؟")) {
      try {
        await deleteCustomer(id);
        success("کڕیار سڕایەوە");
      } catch (err) {
        console.error("Failed to delete customer:", err);
        showError("هەڵەیەک ڕوویدا لە سڕینەوەی کڕیار");
      }
    }
  };

  const filteredCustomers = customers.filter((customer) => {
    const search = searchTerm.toLowerCase();
    return (
      customer.fullName.toLowerCase().includes(search) ||
      customer.phone1.includes(search) ||
      customer.phone2.includes(search) ||
      customer.guarantorName.toLowerCase().includes(search) ||
      customer.guarantorPhone.includes(search) ||
      customer.job.toLowerCase().includes(search) ||
      customer.address.toLowerCase().includes(search) ||
      customer.customerCode.toString().includes(search)
    );
  });

  if (dataLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg text-muted-foreground">چاوەڕوان بە...</div>
      </div>
    );
  }

  const existingCodes = customers.map(c => c.customerCode);

  // Calculate totals for IQD
  const iqdProducts = products.filter(p => p.currency === "IQD");
  const totalSaleIQD = iqdProducts.reduce((sum, p) => sum + p.salePrice, 0);
  const totalPaidIQD = iqdProducts.reduce((sum, p) => sum + p.paidAmount, 0);
  const totalRemainingIQD = iqdProducts.reduce((sum, p) => sum + p.remainingAmount, 0);

  // Calculate totals for USD
  const usdProducts = products.filter(p => p.currency === "USD");
  const totalSaleUSD = usdProducts.reduce((sum, p) => sum + p.salePrice, 0);
  const totalPaidUSD = usdProducts.reduce((sum, p) => sum + p.paidAmount, 0);
  const totalRemainingUSD = usdProducts.reduce((sum, p) => sum + p.remainingAmount, 0);

  return (
    <div className="min-h-screen bg-background flex flex-col md:flex-row" dir="rtl">
      {/* Real-time notifications */}
      <RealtimeNotifications />
      
      {/* Mobile Header */}
      <header className="md:hidden border-b sticky top-0 z-50 bg-background">
        <div className="flex items-center justify-between h-14 px-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
            <span className="font-bold text-base">
              {activePage === "dashboard" && "داشبۆرد"}
              {activePage === "customers" && "کڕیارەکان"}
              {activePage === "sales" && "فرۆشتنەکان"}
              {activePage === "payments" && "پارەدان"}
              {activePage === "expenses" && "خەرجییەکان"}
              {activePage === "notifications" && "دواکەوتووەکان"}
              {activePage === "reports" && "ڕاپۆرتەکان"}
            </span>
          </div>
          <div className="flex items-center gap-1">
            {activePage === "customers" && (
              <>
                <AddCustomerDialog onAdd={handleAddCustomer} existingCodes={existingCodes} jobs={jobs} />
                <AddJobDialog jobs={jobs} onAddJob={handleAddJob} onDeleteJob={handleDeleteJob} customerCountByJob={customerCountByJob} />
              </>
            )}
            {activePage === "sales" && (
              <MultiSaleDialog customers={customers} onAdd={handleAddProduct} />
            )}
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div 
        className={`md:hidden fixed inset-0 top-14 z-40 transition-all duration-300 ease-out ${
          mobileMenuOpen 
            ? 'opacity-100 pointer-events-auto' 
            : 'opacity-0 pointer-events-none'
        }`}
        onClick={(e) => {
          // Close menu when clicking on overlay background
          if (e.target === e.currentTarget) {
            setMobileMenuOpen(false);
          }
        }}
        onTouchMove={(e) => {
          // Prevent scrolling on touch devices
          if (mobileMenuOpen) e.preventDefault();
        }}
      >
        {/* Backdrop */}
        <div 
          className={`absolute inset-0 bg-background/80 backdrop-blur-sm transition-opacity duration-300 ${
            mobileMenuOpen ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={() => setMobileMenuOpen(false)}
        />
        
        {/* Menu Panel */}
        <div 
          className={`absolute top-0 right-0 h-full w-72 bg-card border-l shadow-2xl transform transition-transform duration-300 ease-out ${
            mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Logo Header in Mobile Menu */}
          <div className="flex items-center gap-3 h-16 px-4 border-b">
            <div className="h-10 w-10 rounded-xl bg-primary flex items-center justify-center shadow-md">
              <span className="text-primary-foreground font-bold text-xl">ق</span>
            </div>
            <div className="flex flex-col">
              <span className="font-bold text-sm">پێشانگای قەندیل</span>
              <span className="text-xs text-muted-foreground">سیستەمی بەڕێوەبردن</span>
            </div>
          </div>
          <nav className="p-4 space-y-2">
            <button 
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${activePage === "dashboard" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted"}`}
              onClick={() => { setActivePage("dashboard"); setMobileMenuOpen(false); }}
            >
              <LayoutDashboard className="h-5 w-5" />
              داشبۆرد
            </button>
            <button 
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${activePage === "customers" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted"}`}
              onClick={() => { setActivePage("customers"); setMobileMenuOpen(false); }}
            >
              <Users className="h-5 w-5" />
              کڕیارەکان
            </button>
            <button 
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${activePage === "sales" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted"}`}
              onClick={() => { setActivePage("sales"); setMobileMenuOpen(false); }}
            >
              <ShoppingCart className="h-5 w-5" />
              فرۆشتنەکان
            </button>
            <button 
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${activePage === "payments" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted"}`}
              onClick={() => { setActivePage("payments"); setMobileMenuOpen(false); }}
            >
              <CreditCard className="h-5 w-5" />
              پارەدان
            </button>
            <button 
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${activePage === "expenses" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted"}`}
              onClick={() => { setActivePage("expenses"); setMobileMenuOpen(false); }}
            >
              <Receipt className="h-5 w-5" />
              خەرجییەکان
            </button>
            <button 
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${activePage === "notifications" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted"}`}
              onClick={() => { setActivePage("notifications"); setMobileMenuOpen(false); }}
            >
              <Bell className="h-5 w-5" />
              دواکەوتووەکان
            </button>
            <button 
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${activePage === "reports" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted"}`}
              onClick={() => { setActivePage("reports"); setMobileMenuOpen(false); }}
            >
              <FileBarChart className="h-5 w-5" />
              ڕاپۆرتەکان
            </button>
            <div className="border-t my-2" />
            <button 
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted transition-all duration-200"
              onClick={() => { setSettingsOpen(true); setMobileMenuOpen(false); }}
            >
              <Settings className="h-5 w-5" />
              ڕێکخستن
            </button>
            <button className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted transition-all duration-200" onClick={onLogout}>
              <LogOut className="h-5 w-5" />
              چوونەدەرەوە
            </button>
          </nav>
        </div>
      </div>

      {/* Desktop Sidebar */}
      <aside className="hidden md:flex w-56 border-l bg-white dark:bg-muted/30 flex-col h-screen sticky top-0">
        <div className="flex items-center gap-3 h-16 px-4 border-b">
          <div className="h-10 w-10 rounded-xl bg-primary flex items-center justify-center shadow-md">
            <span className="text-primary-foreground font-bold text-xl">ق</span>
          </div>
          <div className="flex flex-col">
            <span className="font-bold text-sm">پێشانگای قەندیل</span>
            <span className="text-xs text-muted-foreground">سیستەمی بەڕێوەبردن</span>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          <button 
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium cursor-pointer ${activePage === "dashboard" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted"}`}
            onClick={() => setActivePage("dashboard")}
          >
            <LayoutDashboard className="h-4 w-4" />
            داشبۆرد
          </button>
          <button 
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium cursor-pointer ${activePage === "customers" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted"}`}
            onClick={() => setActivePage("customers")}
          >
            <Users className="h-4 w-4" />
            کڕیارەکان
          </button>
          <button 
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium cursor-pointer ${activePage === "sales" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted"}`}
            onClick={() => setActivePage("sales")}
          >
            <ShoppingCart className="h-4 w-4" />
            فرۆشتنەکان
          </button>
          <button 
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium cursor-pointer ${activePage === "payments" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted"}`}
            onClick={() => setActivePage("payments")}
          >
            <CreditCard className="h-4 w-4" />
            پارەدان
          </button>
          <button 
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium cursor-pointer ${activePage === "expenses" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted"}`}
            onClick={() => setActivePage("expenses")}
          >
            <Receipt className="h-4 w-4" />
            خەرجییەکان
          </button>
          <button 
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium cursor-pointer ${activePage === "notifications" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted"}`}
            onClick={() => setActivePage("notifications")}
          >
            <Bell className="h-4 w-4" />
            دواکەوتووەکان
          </button>
          <button 
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium cursor-pointer ${activePage === "reports" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted"}`}
            onClick={() => setActivePage("reports")}
          >
            <FileBarChart className="h-4 w-4" />
            ڕاپۆرتەکان
          </button>
          <button className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:bg-muted cursor-pointer" onClick={() => setSettingsOpen(true)}>
            <Settings className="h-4 w-4" />
            ڕێکخستن
          </button>
        </nav>
        <div className="p-3 border-t">
          <button className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:bg-muted cursor-pointer" onClick={onLogout}>
            <LogOut className="h-4 w-4" />
            چوونەدەرەوە
          </button>
        </div>
      </aside>

      {/* Main Area */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Desktop Header */}
        <header className="hidden md:block border-b sticky top-0 z-50 bg-background">
          <div className="flex items-center justify-between h-16 px-6">
            <div className="flex items-center gap-4 min-w-0 flex-shrink">
              <span className="font-semibold truncate">
                {activePage === "dashboard" && "داشبۆرد"}
                {activePage === "customers" && (
                  <span className="flex items-center gap-3">
                    کڕیارەکان
                    <Badge variant="secondary" className="text-xs">
                      {customers.length} کڕیار تۆمارکراو
                    </Badge>
                  </span>
                )}
                {activePage === "payments" && (
                  <span className="flex items-center gap-3">
                    پارەدان
                    <Badge variant="secondary" className="text-xs">
                      {products.filter(p => p.remainingAmount > 0).length} قیست ماوە
                    </Badge>
                  </span>
                )}
                {activePage === "sales" && (
                  <span className="flex items-center gap-3">
                    فرۆشتنەکان
                    <Badge variant="secondary" className="text-xs">
                      {products.length} فرۆشتن
                    </Badge>
                  </span>
                )}
                {activePage === "expenses" && "خەرجییەکان"}
                {activePage === "notifications" && "دواکەوتووەکان"}
                {activePage === "reports" && "ڕاپۆرتەکان"}
              </span>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <RealtimeStatus isConnected={isRealtimeConnected} isEnabled={isRealtimeEnabled} />
              <PWAInstallButton variant="outline" size="sm" />
              {activePage === "customers" && (
                <>
                  <AddCustomerDialog onAdd={handleAddCustomer} existingCodes={existingCodes} jobs={jobs} />
                  <AddJobDialog jobs={jobs} onAddJob={handleAddJob} onDeleteJob={handleDeleteJob} customerCountByJob={customerCountByJob} />
                </>
              )}
              {activePage === "sales" && (
                <MultiSaleDialog customers={customers} onAdd={handleAddProduct} />
              )}
            </div>
          </div>
        </header>

      {/* Main Content */}
      <main className="p-4 md:p-6 space-y-4">
        {activePage === "dashboard" && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <LayoutDashboard className="h-5 w-5" />
                داشبۆرد
              </CardTitle>
              <CardDescription>
                پوختەی گشتی سیستەم
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-12 text-muted-foreground">
                بەشی داشبۆرد بەتاڵە
              </div>
            </CardContent>
          </Card>
        )}

        {activePage === "customers" && (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {/* Total Customers */}
              <div className="rounded-xl border bg-card p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="h-8 w-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
                    <Users className="h-4 w-4 text-blue-600" />
                  </div>
                </div>
                <p className="text-2xl font-bold">{customers.length}</p>
                <p className="text-xs text-muted-foreground">کۆی کڕیارەکان</p>
              </div>

              {/* Blocked Customers */}
              <div className="rounded-xl border bg-card p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="h-8 w-8 rounded-lg bg-red-500/10 flex items-center justify-center">
                    <Shield className="h-4 w-4 text-red-600" />
                  </div>
                </div>
                <p className="text-2xl font-bold text-red-500">{customers.filter(c => c.isBlocked).length}</p>
                <p className="text-xs text-muted-foreground">بلۆک کراو</p>
              </div>
              {/* Customers with Active Installments */}
              <div className="rounded-xl border bg-card p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="h-8 w-8 rounded-lg bg-amber-500/10 flex items-center justify-center">
                    <CreditCard className="h-4 w-4 text-amber-600" />
                  </div>
                </div>
                <p className="text-2xl font-bold text-amber-600">
                  {customers.filter(c => products.filter(p => p.customerId === c.id && p.remainingAmount > 0).length > 0).length}
                </p>
                <p className="text-xs text-muted-foreground">کڕیار قیستی لایە</p>
              </div>

              {/* Customers with Completed Installments */}
              <div className="rounded-xl border bg-card p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="h-8 w-8 rounded-lg bg-green-500/10 flex items-center justify-center">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                  </div>
                </div>
                <p className="text-2xl font-bold text-green-600">
                  {customers.filter(c => products.filter(p => p.customerId === c.id && p.remainingAmount > 0).length === 0).length}
                </p>
                <p className="text-xs text-muted-foreground">کڕیار قیستی تەواوبوو</p>
              </div>
            </div>

            <Card>
              <CardHeader className="pb-3">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                  <CardTitle className="flex items-center gap-2 text-base md:text-lg">
                    <Users className="h-5 w-5" />
                    لیستی کڕیارەکان
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    {searchTerm && (
                      <p className="text-sm text-muted-foreground hidden md:block">
                        {filteredCustomers.length} ئەنجام
                      </p>
                    )}
                    <div className="relative flex-1 md:w-64">
                      <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="گەڕان..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pr-9"
                      />
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="px-2 md:px-6">
                <CustomerTable 
                  customers={filteredCustomers} 
                  products={products}
                  jobs={jobs}
                  onDelete={handleDeleteCustomer}
                  onUpdateCustomer={handleUpdateCustomer}
                  onAddProduct={handleAddProduct}
                  onDeleteProduct={handleDeleteProduct}
                  onUpdateProduct={handleUpdateProduct}
                  onNavigate={(page) => {
                    if (page === "settings") {
                      setSettingsOpen(true);
                    } else {
                      setActivePage(page);
                    }
                  }}
                  mode="customers"
                />
              </CardContent>
            </Card>
          </>
        )}

        {activePage === "payments" && (
          <>
            {/* Summary Cards - Minimal Design */}
            <div className="grid grid-cols-2 gap-3 md:gap-4">
              {/* IQD Card */}
              <div className="rounded-xl border bg-card p-4">
                <div className="flex items-center gap-2 mb-4">
                  <div className="h-8 w-8 rounded-lg bg-amber-500/10 flex items-center justify-center">
                    <Banknote className="h-4 w-4 text-amber-600" />
                  </div>
                  <span className="font-semibold">دینار</span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">فرۆشتن</span>
                    <span className="font-medium" dir="ltr">{totalSaleIQD.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">وەرگیراو</span>
                    <span className="font-medium text-green-600" dir="ltr">{totalPaidIQD.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">ماوە</span>
                    <span className="font-medium text-red-500" dir="ltr">{totalRemainingIQD.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {/* USD Card */}
              <div className="rounded-xl border bg-card p-4">
                <div className="flex items-center gap-2 mb-4">
                  <div className="h-8 w-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                    <DollarSign className="h-4 w-4 text-emerald-600" />
                  </div>
                  <span className="font-semibold">دۆلار</span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">فرۆشتن</span>
                    <span className="font-medium" dir="ltr">{totalSaleUSD.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">وەرگیراو</span>
                    <span className="font-medium text-green-600" dir="ltr">{totalPaidUSD.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">ماوە</span>
                    <span className="font-medium text-red-500" dir="ltr">{totalRemainingUSD.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>

            <Card>
              <CardHeader className="pb-3">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                  {/* Title */}
                  <CardTitle className="flex items-center gap-2 text-base md:text-lg">
                    <CreditCard className="h-5 w-5" />
                    لیستی پارەدانەکان
                  </CardTitle>
                  {/* Search Bar and Results */}
                  <div className="flex items-center gap-2">
                    {searchTerm && (
                      <p className="text-sm text-muted-foreground hidden md:block">
                        {filteredCustomers.length} ئەنجام
                      </p>
                    )}
                    <div className="relative flex-1 md:w-64">
                      <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="گەڕان..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pr-9"
                      />
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="px-2 md:px-6">
                <PaymentsTable 
                  customers={filteredCustomers} 
                  products={products}
                  jobs={jobs}
                  onAddProduct={handleAddProduct}
                  onDeleteProduct={handleDeleteProduct}
                  onUpdateProduct={handleUpdateProduct}
                  onNavigate={(page) => {
                    if (page === "settings") {
                      setSettingsOpen(true);
                    } else {
                      setActivePage(page);
                    }
                  }}
                  onSelectCustomer={(customer) => {
                    setPaymentsSelectedCustomer(customer);
                    setPaymentsProfileOpen(true);
                  }}
                />
              </CardContent>
            </Card>

            {/* Customer Payment Sheet for Payments */}
            <CustomerPaymentSheet
              customer={paymentsSelectedCustomer}
              products={products}
              open={paymentsProfileOpen}
              onClose={() => setPaymentsProfileOpen(false)}
              onAddProduct={handleAddProduct}
              onDeleteProduct={handleDeleteProduct}
              onUpdateProduct={handleUpdateProduct}
              onNavigate={(page) => {
                if (page === "settings") {
                  setSettingsOpen(true);
                } else {
                  setActivePage(page);
                }
              }}
              hideAddProduct={true}
            />
          </>
        )}

        {activePage === "sales" && (
          <>
            {/* Summary Cards - Minimal Design */}
            <div className="grid grid-cols-2 gap-3 md:gap-4">
              {/* IQD Card */}
              <div className="rounded-xl border bg-card p-4">
                <div className="flex items-center gap-2 mb-4">
                  <div className="h-8 w-8 rounded-lg bg-amber-500/10 flex items-center justify-center">
                    <Banknote className="h-4 w-4 text-amber-600" />
                  </div>
                  <span className="font-semibold">دینار</span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">فرۆشتن</span>
                    <span className="font-medium" dir="ltr">{products.filter(p => p.currency === "IQD").reduce((sum, p) => sum + p.salePrice, 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">وەرگیراو</span>
                    <span className="font-medium text-green-600" dir="ltr">{products.filter(p => p.currency === "IQD").reduce((sum, p) => sum + p.paidAmount, 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">ماوە</span>
                    <span className="font-medium text-red-500" dir="ltr">{products.filter(p => p.currency === "IQD").reduce((sum, p) => sum + p.remainingAmount, 0).toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {/* USD Card */}
              <div className="rounded-xl border bg-card p-4">
                <div className="flex items-center gap-2 mb-4">
                  <div className="h-8 w-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                    <DollarSign className="h-4 w-4 text-emerald-600" />
                  </div>
                  <span className="font-semibold">دۆلار</span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">فرۆشتن</span>
                    <span className="font-medium" dir="ltr">{products.filter(p => p.currency === "USD").reduce((sum, p) => sum + p.salePrice, 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">وەرگیراو</span>
                    <span className="font-medium text-green-600" dir="ltr">{products.filter(p => p.currency === "USD").reduce((sum, p) => sum + p.paidAmount, 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">ماوە</span>
                    <span className="font-medium text-red-500" dir="ltr">{products.filter(p => p.currency === "USD").reduce((sum, p) => sum + p.remainingAmount, 0).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>

            <Tabs defaultValue="active" className="w-full" dir="rtl">
              <TabsList className="w-full h-auto p-1 bg-muted/50 rounded-lg border gap-1 grid grid-cols-2">
                <TabsTrigger 
                  value="active" 
                  className="relative flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium text-muted-foreground transition-all duration-200 cursor-pointer hover:text-foreground hover:bg-background/60 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground dark:data-[state=active]:bg-primary dark:data-[state=active]:text-primary-foreground data-[state=active]:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                >
                  <ShoppingCart className="h-4 w-4" />
                  <span>فرۆشراوەکان</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="completed" 
                  className="relative flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium text-muted-foreground transition-all duration-200 cursor-pointer hover:text-foreground hover:bg-background/60 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground dark:data-[state=active]:bg-primary dark:data-[state=active]:text-primary-foreground data-[state=active]:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                >
                  <CheckCircle className="h-4 w-4" />
                  <span>تەواوبووەکان</span>
                </TabsTrigger>
              </TabsList>

              <div className="mt-3 rounded-lg border bg-card p-3 md:p-4" dir="rtl">
                <TabsContent value="active" className="mt-0">
                  {(() => {
                    const activeProducts = [...products.filter(p => p.remainingAmount > 0)].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
                    if (activeProducts.length === 0) {
                      return (
                        <div className="text-center py-12 text-muted-foreground">
                          هیچ فرۆشتنێکی چالاک نییە
                        </div>
                      );
                    }
                    return (
                      <>
                        {/* Header with count */}
                        <div className="flex items-center justify-between mb-3 pb-2 border-b">
                          <span className="text-sm text-muted-foreground">کۆی فرۆشتنەکان: <span className="font-medium text-foreground">{activeProducts.length}</span></span>
                        </div>
                        {/* Desktop Table */}
                        <div className="rounded-md border hidden md:block">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="text-right w-12">#</TableHead>
                                <TableHead className="text-right">کڕیار</TableHead>
                                <TableHead className="text-right">ناوی ئامێر</TableHead>
                                <TableHead className="text-right">نرخی فرۆشتن</TableHead>
                                <TableHead className="text-right">پارەی وەرگیراو</TableHead>
                                <TableHead className="text-right">پارەی ماوە</TableHead>
                                <TableHead className="text-right">بەرواری کڕین</TableHead>
                                <TableHead className="text-right w-fit"></TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {activeProducts.map((product, index) => {
                                const customer = customers.find(c => c.id === product.customerId);
                                return (
                                  <TableRow key={`${product.id}-${index}`} className={index % 2 === 1 ? 'bg-muted/50' : ''}>
                                    <TableCell className="text-center font-medium text-muted-foreground">
                                      {index + 1}
                                    </TableCell>
                                    <TableCell className="font-medium">
                                      {customer?.fullName || "-"}
                                    </TableCell>
                                    <TableCell>
                                      <div>
                                        <span className="font-medium">{product.deviceName}</span>
                                        {product.deviceDescription && (
                                          <p className="text-sm text-muted-foreground">{product.deviceDescription}</p>
                                        )}
                                      </div>
                                    </TableCell>
                                    <TableCell dir="ltr" className="text-right">
                                      {product.salePrice.toLocaleString()}{" "}
                                      <Badge variant="outline" className="mr-1">
                                        {product.currency === "IQD" ? "د.ع" : "$"}
                                      </Badge>
                                    </TableCell>
                                    <TableCell dir="ltr" className="text-right text-green-600">
                                      {product.paidAmount.toLocaleString()}{" "}
                                      <Badge variant="secondary" className="mr-1">
                                        {product.currency === "IQD" ? "د.ع" : "$"}
                                      </Badge>
                                    </TableCell>
                                    <TableCell dir="ltr" className="text-right">
                                      {product.remainingAmount.toLocaleString()}{" "}
                                      <Badge variant="destructive" className="mr-1">
                                        {product.currency === "IQD" ? "د.ع" : "$"}
                                      </Badge>
                                    </TableCell>
                                    <TableCell dir="ltr" className="text-right">{product.date}</TableCell>
                                    <TableCell>
                                      <DropdownMenu>
                                        <DropdownMenuTrigger className="inline-flex items-center justify-center h-8 w-8 rounded-lg border bg-muted/50 hover:bg-muted cursor-pointer">
                                          <MoreHorizontal className="h-4 w-4" />
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent className="text-right w-48">
                                          <DropdownMenuItem 
                                            onSelect={() => {
                                              setSaleToDelete(product.id);
                                              setDeleteConfirmOpen(true);
                                            }}
                                            className="text-destructive focus:text-destructive"
                                          >
                                            <Trash2 className="h-4 w-4 ml-2" />
                                            سڕینەوە
                                          </DropdownMenuItem>
                                        </DropdownMenuContent>
                                      </DropdownMenu>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </div>

                        {/* Mobile Cards */}
                        <div className="md:hidden space-y-3">
                          {activeProducts.map((product, index) => {
                            const customer = customers.find(c => c.id === product.customerId);
                            const isExpanded = expandedSales.has(product.id);
                            const isEven = index % 2 === 1;
                            return (
                              <div key={`${product.id}-${index}`} className={`rounded-xl border overflow-hidden transition-all duration-200 ${isExpanded ? 'border-primary ring-1 ring-primary/20' : ''} ${isEven ? 'bg-muted/60' : 'bg-card'}`}>
                                <div className="p-3 cursor-pointer" onClick={() => toggleSaleExpanded(product.id)}>
                                  {/* Main Row */}
                                  <div className="flex items-center gap-3">
                                    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                      <span className="text-sm font-bold text-primary">{index + 1}</span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2">
                                        <span className="font-semibold text-sm truncate">{product.deviceName}</span>
                                        <Badge variant={product.currency === "IQD" ? "outline" : "secondary"} className="text-[10px] px-1.5 h-4">
                                          {product.currency === "IQD" ? "د.ع" : "$"}
                                        </Badge>
                                      </div>
                                      <div className="text-xs text-muted-foreground mt-0.5">
                                        {customer?.fullName || "-"}
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <Button 
                                        variant="ghost" 
                                        size="icon" 
                                        className="h-8 w-8"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleSaleExpanded(product.id);
                                        }}
                                      >
                                        {isExpanded ? (
                                          <ChevronUp className="h-4 w-4" />
                                        ) : (
                                          <ChevronDown className="h-4 w-4" />
                                        )}
                                      </Button>
                                      <DropdownMenu>
                                        <DropdownMenuTrigger onClick={(e) => e.stopPropagation()} className="inline-flex items-center justify-center h-8 w-8 rounded-lg border bg-muted/50 hover:bg-muted cursor-pointer">
                                          <MoreHorizontal className="h-4 w-4" />
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent className="text-right w-48">
                                          <DropdownMenuItem 
                                            onSelect={() => {
                                              setSaleToDelete(product.id);
                                              setDeleteConfirmOpen(true);
                                            }}
                                            className="text-destructive focus:text-destructive"
                                          >
                                            <Trash2 className="h-4 w-4 ml-2" />
                                            سڕینەوە
                                          </DropdownMenuItem>
                                        </DropdownMenuContent>
                                      </DropdownMenu>
                                    </div>
                                  </div>
                                  {/* Mini Table */}
                                  <div className="grid grid-cols-3 gap-1 mt-2 text-xs bg-muted/30 rounded-lg p-2 w-full" dir="rtl">
                                    <div className="text-center">
                                      <div className="text-muted-foreground text-[10px]">نرخ</div>
                                      <div className="font-semibold" dir={product.currency === "IQD" ? "rtl" : "ltr"}>{product.currency === "IQD" ? <>{product.salePrice.toLocaleString()} <span className="text-[10px] text-muted-foreground">د.ع</span></> : <><span className="text-[10px] text-muted-foreground">$ </span>{product.salePrice.toLocaleString()}</>}</div>
                                    </div>
                                    <div className="text-center border-x border-dashed">
                                      <div className="text-muted-foreground text-[10px]">وەرگیراو</div>
                                      <div className="font-semibold text-green-600" dir={product.currency === "IQD" ? "rtl" : "ltr"}>{product.currency === "IQD" ? <>{product.paidAmount.toLocaleString()} <span className="text-[10px]">د.ع</span></> : <><span className="text-[10px]">$ </span>{product.paidAmount.toLocaleString()}</>}</div>
                                    </div>
                                    <div className="text-center">
                                      <div className="text-muted-foreground text-[10px]">ماوە</div>
                                      <div className="font-semibold text-red-500" dir={product.currency === "IQD" ? "rtl" : "ltr"}>{product.currency === "IQD" ? <>{product.remainingAmount.toLocaleString()} <span className="text-[10px]">د.ع</span></> : <><span className="text-[10px]">$ </span>{product.remainingAmount.toLocaleString()}</>}</div>
                                    </div>
                                  </div>
                                </div>
                                {isExpanded && (
                                  <div className="px-3 pb-3 pt-0 border-t bg-muted/30" dir="rtl">
                                    <div className="space-y-2 pt-3">
                                      <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                                        <span className="text-muted-foreground text-xs">قیستی مانگانە</span>
                                        <span className="font-medium text-sm" dir={product.currency === "IQD" ? "rtl" : "ltr"}>{product.currency === "IQD" ? <>{product.monthlyInstallment.toLocaleString()} <span className="text-[10px] text-muted-foreground">د.ع</span></> : <><span className="text-[10px] text-muted-foreground">$ </span>{product.monthlyInstallment.toLocaleString()}</>}</span>
                                      </div>
                                      <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                                        <span className="text-muted-foreground text-xs">ژمارەی قیستەکان</span>
                                        <span className="font-medium text-sm">{product.installments || 0}</span>
                                      </div>
                                      <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                                        <span className="text-muted-foreground text-xs">بەروار</span>
                                        <span className="font-medium text-sm" dir="ltr">{product.date}</span>
                                      </div>
                                      {product.deviceDescription && (
                                        <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                                          <span className="text-muted-foreground text-xs">وەسف</span>
                                          <span className="font-medium text-sm">{product.deviceDescription}</span>
                                        </div>
                                      )}
                                      {product.notes && (
                                        <div className="pt-2 mt-1 border-t">
                                          <span className="text-muted-foreground text-xs block mb-1">تێبینی</span>
                                          <p className="text-sm bg-muted/50 rounded-lg p-2 text-muted-foreground">{product.notes}</p>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </>
                    );
                  })()}
                </TabsContent>

              <TabsContent value="completed" className="mt-0">
                {(() => {
                  const completedProducts = [...products.filter(p => p.remainingAmount <= 0)].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
                  if (completedProducts.length === 0) {
                    return (
                      <div className="text-center py-12 text-muted-foreground">
                        هیچ فرۆشتنێکی تەواوبوو نییە
                      </div>
                    );
                  }
                  return (
                    <>
                      {/* Header with count */}
                      <div className="flex items-center justify-between mb-3 pb-2 border-b">
                        <span className="text-sm text-muted-foreground">کۆی تەواوبووەکان: <span className="font-medium text-foreground">{completedProducts.length}</span></span>
                      </div>
                      {/* Desktop Table */}
                      <div className="rounded-md border hidden md:block">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="text-right w-12">#</TableHead>
                              <TableHead className="text-right">کڕیار</TableHead>
                              <TableHead className="text-right">ناوی ئامێر</TableHead>
                              <TableHead className="text-right">نرخی فرۆشتن</TableHead>
                              <TableHead className="text-right">پارەی وەرگیراو</TableHead>
                              <TableHead className="text-right">بەرواری کڕین</TableHead>
                              <TableHead className="text-right w-fit"></TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {completedProducts.map((product, index) => {
                              const customer = customers.find(c => c.id === product.customerId);
                              return (
                                <TableRow key={`${product.id}-${index}`} className={index % 2 === 1 ? 'bg-muted/50' : ''}>
                                  <TableCell className="text-center font-medium text-muted-foreground">
                                    {index + 1}
                                  </TableCell>
                                  <TableCell className="font-medium">
                                    {customer?.fullName || "-"}
                                  </TableCell>
                                  <TableCell>
                                    <div>
                                      <span className="font-medium">{product.deviceName}</span>
                                      {product.deviceDescription && (
                                        <p className="text-sm text-muted-foreground">{product.deviceDescription}</p>
                                      )}
                                    </div>
                                  </TableCell>
                                  <TableCell dir="ltr" className="text-right">
                                    {product.salePrice.toLocaleString()}{" "}
                                    <Badge variant="outline" className="mr-1">
                                      {product.currency === "IQD" ? "د.ع" : "$"}
                                    </Badge>
                                  </TableCell>
                                  <TableCell dir="ltr" className="text-right text-green-600">
                                    {product.paidAmount.toLocaleString()}{" "}
                                    <Badge variant="secondary" className="mr-1">
                                      {product.currency === "IQD" ? "د.ع" : "$"}
                                    </Badge>
                                  </TableCell>
                                  <TableCell dir="ltr" className="text-right">{product.date}</TableCell>
                                  <TableCell>
                                    <DropdownMenu>
                                      <DropdownMenuTrigger className="inline-flex items-center justify-center h-8 w-8 rounded-lg border bg-muted/50 hover:bg-muted cursor-pointer">
                                        <MoreHorizontal className="h-4 w-4" />
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent className="text-right w-48">
                                        <DropdownMenuItem 
                                          onSelect={() => {
                                            setSaleToDelete(product.id);
                                            setDeleteConfirmOpen(true);
                                          }}
                                          className="text-destructive focus:text-destructive"
                                        >
                                          <Trash2 className="h-4 w-4 ml-2" />
                                          سڕینەوە
                                        </DropdownMenuItem>
                                      </DropdownMenuContent>
                                    </DropdownMenu>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>

                      {/* Mobile Cards */}
                      <div className="md:hidden space-y-3">
                        {completedProducts.map((product, index) => {
                          const customer = customers.find(c => c.id === product.customerId);
                          const isExpanded = expandedSales.has(product.id);
                          const isEven = index % 2 === 1;
                          return (
                            <div key={`${product.id}-${index}`} className={`rounded-xl border overflow-hidden transition-all duration-200 ${isExpanded ? 'border-primary ring-1 ring-primary/20' : ''} ${isEven ? 'bg-muted/60' : 'bg-card'}`}>
                              <div className="p-3 cursor-pointer" onClick={() => toggleSaleExpanded(product.id)}>
                                {/* Main Row */}
                                <div className="flex items-center gap-3">
                                  <div className="h-10 w-10 rounded-full bg-green-500/10 flex items-center justify-center flex-shrink-0">
                                    <CheckCircle className="h-5 w-5 text-green-600" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <span className="font-semibold text-sm truncate">{product.deviceName}</span>
                                      <Badge variant={product.currency === "IQD" ? "outline" : "secondary"} className="text-[10px] px-1.5 h-4">
                                        {product.currency === "IQD" ? "د.ع" : "$"}
                                      </Badge>
                                    </div>
                                    <div className="text-xs text-muted-foreground mt-0.5">
                                      {customer?.fullName || "-"}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <Button 
                                      variant="ghost" 
                                      size="icon" 
                                      className="h-8 w-8"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleSaleExpanded(product.id);
                                      }}
                                    >
                                      {isExpanded ? (
                                        <ChevronUp className="h-4 w-4" />
                                      ) : (
                                        <ChevronDown className="h-4 w-4" />
                                      )}
                                    </Button>
                                    <DropdownMenu>
                                      <DropdownMenuTrigger onClick={(e) => e.stopPropagation()} className="inline-flex items-center justify-center h-8 w-8 rounded-lg border bg-muted/50 hover:bg-muted cursor-pointer">
                                        <MoreHorizontal className="h-4 w-4" />
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent className="text-right w-48">
                                        <DropdownMenuItem 
                                          onSelect={() => {
                                            setSaleToDelete(product.id);
                                            setDeleteConfirmOpen(true);
                                          }}
                                          className="text-destructive focus:text-destructive"
                                        >
                                          <Trash2 className="h-4 w-4 ml-2" />
                                          سڕینەوە
                                        </DropdownMenuItem>
                                      </DropdownMenuContent>
                                    </DropdownMenu>
                                  </div>
                                </div>
                                {/* Mini Table */}
                                <div className="grid grid-cols-3 gap-1 mt-2 text-xs bg-muted/30 rounded-lg p-2 w-full" dir="rtl">
                                  <div className="text-center">
                                    <div className="text-muted-foreground text-[10px]">نرخ</div>
                                    <div className="font-semibold" dir={product.currency === "IQD" ? "rtl" : "ltr"}>{product.currency === "IQD" ? <>{product.salePrice.toLocaleString()} <span className="text-[10px] text-muted-foreground">د.ع</span></> : <><span className="text-[10px] text-muted-foreground">$ </span>{product.salePrice.toLocaleString()}</>}</div>
                                  </div>
                                  <div className="text-center border-x border-dashed">
                                    <div className="text-muted-foreground text-[10px]">وەرگیراو</div>
                                    <div className="font-semibold text-green-600" dir={product.currency === "IQD" ? "rtl" : "ltr"}>{product.currency === "IQD" ? <>{product.paidAmount.toLocaleString()} <span className="text-[10px]">د.ع</span></> : <><span className="text-[10px]">$ </span>{product.paidAmount.toLocaleString()}</>}</div>
                                  </div>
                                  <div className="text-center">
                                    <div className="text-muted-foreground text-[10px]">دۆخ</div>
                                    <Badge variant="default" className="text-[10px] px-1.5 h-4 bg-green-600">تەواو</Badge>
                                  </div>
                                </div>
                              </div>
                              {isExpanded && (
                                <div className="px-3 pb-3 pt-0 border-t bg-muted/30" dir="rtl">
                                  <div className="space-y-2 pt-3">
                                    <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                                      <span className="text-muted-foreground text-xs">قیستی مانگانە</span>
                                      <span className="font-medium text-sm" dir={product.currency === "IQD" ? "rtl" : "ltr"}>{product.currency === "IQD" ? <>{product.monthlyInstallment.toLocaleString()} <span className="text-[10px] text-muted-foreground">د.ع</span></> : <><span className="text-[10px] text-muted-foreground">$ </span>{product.monthlyInstallment.toLocaleString()}</>}</span>
                                    </div>
                                    <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                                      <span className="text-muted-foreground text-xs">ژمارەی قیستەکان</span>
                                      <span className="font-medium text-sm">{product.installments || 0}</span>
                                    </div>
                                    <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                                      <span className="text-muted-foreground text-xs">بەروار</span>
                                      <span className="font-medium text-sm" dir="ltr">{product.date}</span>
                                    </div>
                                    {product.deviceDescription && (
                                      <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                                        <span className="text-muted-foreground text-xs">وەسف</span>
                                        <span className="font-medium text-sm">{product.deviceDescription}</span>
                                      </div>
                                    )}
                                    {product.notes && (
                                      <div className="pt-2 mt-1 border-t">
                                        <span className="text-muted-foreground text-xs block mb-1">تێبینی</span>
                                        <p className="text-sm bg-muted/50 rounded-lg p-2 text-muted-foreground">{product.notes}</p>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </>
                  );
                })()}
              </TabsContent>
            </div>
          </Tabs>

          {/* Delete Confirmation Dialog */}
          <ConfirmDialog
            open={deleteConfirmOpen}
            onOpenChange={(open) => {
              setDeleteConfirmOpen(open);
              if (!open) setSaleToDelete(null);
            }}
            title="سڕینەوەی فرۆشتن"
            description="دڵنیایت لە سڕینەوەی ئەم فرۆشتنە؟ ئەم کردارە ناگەڕێتەوە."
            confirmText="سڕینەوە"
            cancelText="پاشگەزبوونەوە"
            variant="destructive"
            onConfirm={() => {
              if (saleToDelete) {
                handleDeleteProduct(saleToDelete);
                setSaleToDelete(null);
                setDeleteConfirmOpen(false);
              }
            }}
          />
          </>
        )}

        {activePage === "expenses" && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Receipt className="h-5 w-5" />
                خەرجییەکان
              </CardTitle>
              <CardDescription>
                بەڕێوەبردنی خەرجییەکان
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-12 text-muted-foreground">
                بەشی خەرجییەکان بەتاڵە
              </div>
            </CardContent>
          </Card>
        )}

        {activePage === "reports" && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileBarChart className="h-5 w-5" />
                ڕاپۆرتەکان
              </CardTitle>
              <CardDescription>
                ڕاپۆرتی فرۆشتن و داهات
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-12 text-muted-foreground">
                بەشی ڕاپۆرتەکان بەتاڵە
              </div>
            </CardContent>
          </Card>
        )}

        {activePage === "notifications" && (
              <OverdueTable
                customers={customers}
                products={products}
                onAddProduct={handleAddProduct}
                onDeleteProduct={handleDeleteProduct}
                onUpdateProduct={handleUpdateProduct}
                onNavigate={(page) => {
                  if (page === "settings") {
                    setSettingsOpen(true);
                  } else {
                    setActivePage(page);
                  }
                }}
              />
        )}
      </main>
      </div>

      {/* Settings Page */}
      <SettingsPage open={settingsOpen} onClose={() => setSettingsOpen(false)} onNavigate={(page) => setActivePage(page)} />
    </div>
  );
}
